import { monthLabelOf } from '../tabs/pricingSummary/pricingSummaryModel';
import type { OtaSummaryMonth, OtaSummaryOption, QuartileStat } from '../tabs/pricingSummary/types';

import {
  andList,
  cellAddress,
  cellAt,
  excelErrorOf,
  findLabel,
  isBlank,
  monthKeyOf,
  numberOf,
  positiveNumberOf,
  textOf,
} from './sheetCells';
import type { ImportIssue, SheetContext, SheetRows } from './types';

// The draft schema's range for nights.
const MAX_NIGHTS = 21;
const PRV_TARGET = 0.3;

// Offsets from the "Month" header; Ind. Price is skipped, the tool derives it from Avg OTA.
const MONTH_COLUMNS = {
  medianPrv: { offset: 1, header: 'median prv %' },
  avgOta: { offset: 2, header: 'avg ota' },
  lowOta: { offset: 3, header: 'low ota' },
  leSell: { offset: 5, header: 'le sell' },
} as const;

function headerOf(value: unknown): string | undefined {
  return textOf(value)?.replace(/\s+/g, ' ').toLowerCase();
}

function pctOf(value: unknown): number | null {
  const number = numberOf(value);
  return number === null ? null : number * 100;
}

function quartileStat(
  rows: SheetRows,
  row: number,
  avgCol: number,
  quartileCols: Array<number | undefined>,
): QuartileStat {
  const [q1Pct, medianPct, q3Pct] = quartileCols.map((col) =>
    col === undefined ? null : pctOf(cellAt(rows, row, col)),
  );
  return { avgPct: pctOf(cellAt(rows, row, avgCol)), q1Pct, medianPct, q3Pct };
}

function parseMonths({ rows, sheet, issues }: SheetContext, beforeRow: number): Array<OtaSummaryMonth> {
  const header = findLabel(rows, 'Month', { toRow: beforeRow });
  const columnsMatch =
    header &&
    Object.values(MONTH_COLUMNS).every(
      ({ offset, header: label }) => headerOf(cellAt(rows, header.row, header.col + offset)) === label,
    );
  if (!columnsMatch) {
    issues.push({
      section: 'otaRates',
      severity: 'warning',
      message: `The "${sheet}" sheet has no month table with Month, Median PRV %, Avg OTA, Low OTA and LE SELL, so no months will load.`,
    });
    return [];
  }

  const months: Array<OtaSummaryMonth> = [];
  const erroredMonths: Array<string> = [];
  // The table ends at its first blank month; a day-of-week table sits under it.
  for (let row = header.row + 1; !isBlank(cellAt(rows, row, header.col)); row++) {
    const cell = (column: keyof typeof MONTH_COLUMNS) => cellAt(rows, row, header.col + MONTH_COLUMNS[column].offset);
    const month = monthKeyOf(cellAt(rows, row, header.col));
    if (!month) {
      issues.push({
        section: 'otaRates',
        severity: 'warning',
        cell: cellAddress(row, header.col),
        message: `On row ${row + 1}, the Month isn't a month, so that row won't load.`,
      });
      continue;
    }
    if (Object.values(MONTH_COLUMNS).some(({ offset }) => excelErrorOf(cellAt(rows, row, header.col + offset)))) {
      erroredMonths.push(monthLabelOf(month));
    }
    months.push({
      month,
      medianPrvPct: pctOf(cell('medianPrv')),
      avgOta: positiveNumberOf(cell('avgOta')),
      // An error month shows Low OTA as 0, which is no price at all.
      lowOta: positiveNumberOf(cell('lowOta')),
      leSell: positiveNumberOf(cell('leSell')),
    });
  }
  if (erroredMonths.length) {
    const one = erroredMonths.length === 1;
    issues.push({
      section: 'otaRates',
      severity: 'warning',
      message: `${andList(erroredMonths)} ${one ? 'shows' : 'show'} an Excel error, so ${one ? 'its' : 'their'} figures load blank.`,
    });
  }
  return months;
}

function parseDailyPrv({ rows, sheet, issues }: SheetContext): OtaSummaryOption['dailyPrv'] {
  const counts = { total: 0, below30: 0, negative: 0 };
  const header = findLabel(rows, 'GEN DISCOUNT');
  if (!header) {
    issues.push({
      section: 'otaRates',
      severity: 'warning',
      message: `The "${sheet}" sheet has no GEN DISCOUNT column, so the days below 30% won't load.`,
    });
    return counts;
  }
  for (let row = header.row + 1; row < rows.length; row++) {
    const prv = numberOf(cellAt(rows, row, header.col));
    if (prv !== null) {
      counts.total += 1;
      counts.below30 += prv < PRV_TARGET ? 1 : 0;
      counts.negative += prv < 0 ? 1 : 0;
    }
  }
  return counts;
}

function hasNoFigures({ genuinePrv, dailyPrv, months }: OtaSummaryOption): boolean {
  return (
    genuinePrv.avgPct === null &&
    dailyPrv.total === 0 &&
    months.every(({ medianPrvPct, avgOta, lowOta, leSell }) =>
      [medianPrvPct, avgOta, lowOta, leSell].every((v) => v === null),
    )
  );
}

export function parseOtaRates(context: SheetContext): OtaSummaryOption | null {
  const { rows, sheet } = context;
  const genuine = findLabel(rows, 'Average Genuine Discount');
  if (!genuine) {
    context.issues.push({
      section: 'otaRates',
      severity: 'error',
      message: `The "${sheet}" sheet has no "Average Genuine Discount" row, so no OTA figures will load.`,
    });
    return null;
  }

  // Held back until the sheet turns out to have figures: an unfilled one would list every cell's error.
  const issues: Array<ImportIssue> = [];
  const sheetContext = { rows, sheet, issues };
  const nightsLabel = findLabel(rows, 'Nights in Scrape', { col: genuine.col });
  const typedNights = nightsLabel ? positiveNumberOf(cellAt(rows, nightsLabel.row, nightsLabel.col + 1)) : null;
  const nights =
    typedNights !== null && Number.isInteger(typedNights) && typedNights <= MAX_NIGHTS ? typedNights : null;
  if (nights === null) {
    issues.push({
      section: 'otaRates',
      severity: 'warning',
      message: `Nights in Scrape isn't a whole number of nights from 1 to ${MAX_NIGHTS}, so it loads blank.`,
    });
  }

  const quartileRow = { fromRow: genuine.row - 1, toRow: genuine.row - 1 };
  const quartileCols = ['1 QTR', 'Median', '3 QTR'].map((label) => findLabel(rows, label, quartileRow)?.col);
  const sellVsMinOta = findLabel(rows, 'Average SELL under MIN OTA', { col: genuine.col });
  const avgCol = genuine.col + 2;

  const summary: OtaSummaryOption = {
    nights,
    genuinePrv: quartileStat(rows, genuine.row, avgCol, quartileCols),
    sellVsMinOta: sellVsMinOta
      ? quartileStat(rows, sellVsMinOta.row, avgCol, quartileCols)
      : { avgPct: null, q1Pct: null, medianPct: null, q3Pct: null },
    dailyPrv: parseDailyPrv(sheetContext),
    months: parseMonths(sheetContext, genuine.row),
  };
  // Loading it would replace Option 1's figures with blanks.
  if (hasNoFigures(summary)) {
    context.issues.push({
      section: 'otaRates',
      severity: 'error',
      absent: true,
      message: `The "${sheet}" sheet has no OTA figures yet, so nothing will load.`,
    });
    return null;
  }
  context.issues.push(...issues);
  return summary;
}
